/**
 * useRBAC Composable
 * Comprehensive Role-Based Access Control functionality
 */

import { computed, ref, watch, onMounted, onUnmounted, readonly, getCurrentInstance } from 'vue';
import { useUserStore } from '@/store/user.store';
import { useProjectStore } from '@/store/project.store';
import { useCentralizedDemo } from '@/composables/useCentralizedDemo';
import {
  UserRole,
  Permission,
  RoleCheckResult,
  PermissionCheckResult,
  UserRoleState,
  CustomClaimsFormats,
  RoleChangeEvent,
} from '@/types/rbac';
import {
  RBAC_CONFIG,
  ROLE_DEFINITIONS,
  RBAC_HELPERS,
} from '@/config/rbac';
// NOTE: auth is imported dynamically to prevent Firebase initialization race conditions
// See FIREBASE_INITIALIZATION_FIX.md for details

// Global role state - shared across all composable instances
const globalRoleState = ref<UserRoleState>({
  currentRole: null,
  projectId: null,
  isLoading: false,
  lastUpdated: null,
  customClaims: null,
});

// Role extraction cache to prevent redundant calculations
const roleExtractionCache = new Map<string, { role: UserRole | null, timestamp: number }>();
const CACHE_TTL = 5000; // 5 seconds cache TTL

// Register cache with global cleanup manager
import { registerPerformanceCache } from '@/utils/cache-manager';
registerPerformanceCache('rbac-role-extraction', roleExtractionCache, CACHE_TTL, 100);

// Debug role override state - for testing different roles
const debugRoleOverride = ref<{
  isActive: boolean;
  overrideRole: UserRole | null;
  originalRole: UserRole | null;
  timestamp: Date | null;
}>({
  isActive: false,
  overrideRole: null,
  originalRole: null,
  timestamp: null,
});

// Role change event emitter
const roleChangeCallbacks: Array<(event: RoleChangeEvent) => void> = [];

// Add debounce mechanism for claims refresh
let claimsRefreshTimeout: NodeJS.Timeout | null = null;
let isRefreshingClaims = false;
let lastClaimsRefreshAt: number | null = null;
const MIN_CLAIMS_REFRESH_INTERVAL_MS = 120000; // 2 minutes

export function useRBAC() {
  const userStore = useUserStore();
  const projectStore = useProjectStore();

  // ===== REACTIVE STATE =====

  const isLoading = computed(() => globalRoleState.value.isLoading);
  const currentRole = computed(() => {
    // Return debug override role if active, otherwise return detected role
    if (debugRoleOverride.value.isActive && debugRoleOverride.value.overrideRole) {
      return debugRoleOverride.value.overrideRole;
    }
    return globalRoleState.value.currentRole;
  });
  const currentProjectId = computed(() => globalRoleState.value.projectId);
  const lastUpdated = computed(() => globalRoleState.value.lastUpdated);
  const isDebugOverrideActive = computed(() => debugRoleOverride.value.isActive);
  const debugOverrideInfo = computed(() => ({
    isActive: debugRoleOverride.value.isActive,
    overrideRole: debugRoleOverride.value.overrideRole,
    originalRole: debugRoleOverride.value.originalRole,
    timestamp: debugRoleOverride.value.timestamp,
  }));

  // ===== ROLE DETECTION =====

  /**
   * Extract role from custom claims for a specific project
   */
  function extractRoleFromClaims(claims: any, projectId: string): UserRole | null {
    if (!claims || !projectId) {
      return null;
    }

    // Create cache key
    const cacheKey = `${projectId}:${JSON.stringify(claims)}`;
    const now = Date.now();
    
    // Check cache first
    const cached = roleExtractionCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < CACHE_TTL) {
      // Only log cache hits in debug mode to reduce noise
      if (RBAC_CONFIG.enableDebug) {
        console.log('[RBAC] Using cached role extraction result:', { projectId, role: cached.role });
      }
      return cached.role;
    }

    console.log('[RBAC] === EXTRACTING ROLE FROM CLAIMS ===');
    console.log('[RBAC] Input:', { claims, projectId });

    // Use centralized demo mode detection instead of checking claims directly
    const centralizedDemo = useCentralizedDemo();
    
    console.log('[RBAC] Using centralized demo mode detection:', centralizedDemo.getDemoModeDebugInfo());
    
    // Demo mode override (also respect Shirly admin override)
    if (centralizedDemo.isDemoMode.value && claims.demo === 'admin') {
      // Check if Shirly admin override is active (disabling admin roles for testing)
      const isShirlyOverrideActive = userStore.isShirlyAdminOverrideActive?.();
      if (isShirlyOverrideActive) {
        console.log('[RBAC] Demo mode admin detected, but Shirly override active - skipping demo mode');
        // Continue to project-specific role detection
      } else {
        console.log('[RBAC] Demo mode override - returning admin');
        const adminRole = 'admin' as UserRole;
        roleExtractionCache.set(cacheKey, { role: adminRole, timestamp: now });
        return adminRole;
      }
    }

    // Shirly admin override (can be disabled for testing)
    console.log('[RBAC] About to check Shirly admin:', {
      hasShirlyClaim: !!claims.shirly,
      shirlyClaimValue: claims.shirly,
      isShirlyAdmin: userStore.isShirlyAdmin,
      checkResult: claims.shirly && userStore.isShirlyAdmin
    });
    
    if (claims.shirly && userStore.isShirlyAdmin) {
      // Check if override is disabled for testing
      const isOverrideActive = userStore.isShirlyAdminOverrideActive();
      console.log('[RBAC] Shirly admin check:', {
        hasShirlyClaim: !!claims.shirly,
        isShirlyAdmin: userStore.isShirlyAdmin,
        isOverrideActive,
        projectId
      });
      
      if (isOverrideActive) {
        console.log('[RBAC] Shirly admin override disabled for testing - using project-specific role');
        // Skip Shirly admin logic when override is active - continue to project-specific role detection
      } else {
        console.log('[RBAC] Using Shirly admin role');
        const adminRole = 'admin' as UserRole;
        roleExtractionCache.set(cacheKey, { role: adminRole, timestamp: now });
        return adminRole;
      }
    } else {
      console.log('[RBAC] Shirly admin check failed - continuing to project-specific role');
    }

    // Format 1: Direct project mapping - { "projectId": "role" }
    console.log('[RBAC] Checking direct project mapping:', {
      projectId,
      projectClaim: claims[projectId],
      claimType: typeof claims[projectId]
    });
    
    if (claims[projectId] && typeof claims[projectId] === 'string') {
      console.log('[RBAC] Found direct project role:', claims[projectId]);
      const directRole = claims[projectId] as UserRole;
      roleExtractionCache.set(cacheKey, { role: directRole, timestamp: now });
      return directRole;
    }

    // Format 2: Explicit role mapping - { "projectId:role": "role" }
    const explicitRoleKey = `${projectId}:role`;
    if (claims[explicitRoleKey]) {
      const explicitRole = claims[explicitRoleKey] as UserRole;
      roleExtractionCache.set(cacheKey, { role: explicitRole, timestamp: now });
      return explicitRole;
    }

    // Format 3: Nested project roles - { "projectRoles": { "projectId": "role" } }
    if (claims.projectRoles && claims.projectRoles[projectId]) {
      const nestedRole = claims.projectRoles[projectId] as UserRole;
      roleExtractionCache.set(cacheKey, { role: nestedRole, timestamp: now });
      return nestedRole;
    }

    // Format 4: Nested roles - { "roles": { "projectId": "role" } }
    if (claims.roles && claims.roles[projectId]) {
      const nestedRole = claims.roles[projectId] as UserRole;
      roleExtractionCache.set(cacheKey, { role: nestedRole, timestamp: now });
      return nestedRole;
    }

    // Fallback: check if user has any role claims (default to customer)
    const hasAnyRoleClaim = Object.keys(claims).some(key => 
      key.includes(projectId) || key === 'roles' || key === 'projectRoles'
    );

    const finalRole = hasAnyRoleClaim ? RBAC_CONFIG.defaultRole : null;
    console.log('[RBAC] === FINAL ROLE RESULT ===');
    console.log('[RBAC] Returning role:', finalRole);
    console.log('[RBAC] hasAnyRoleClaim:', hasAnyRoleClaim);
    console.log('[RBAC] defaultRole:', RBAC_CONFIG.defaultRole);
    
    // Cache the result
    roleExtractionCache.set(cacheKey, { role: finalRole, timestamp: now });
    
    return finalRole;
  }

  /**
   * Update role for current project
   */
  async function updateRole(trigger: RoleChangeEvent['trigger'] = 'manual'): Promise<boolean> {
    try {
      globalRoleState.value.isLoading = true;

      const currentProjectId = projectStore.projectId;
      const customClaims = userStore.customClaims;

      if (!currentProjectId) {
        console.warn('[RBAC] No project ID available for role detection');
        setRole(null, null, trigger);
        return false;
      }

      if (!customClaims) {
        console.warn('[RBAC] No custom claims available for role detection');
        setRole(null, currentProjectId, trigger);
        return false;
      }

      const detectedRole = extractRoleFromClaims(customClaims, currentProjectId);
      
      if (RBAC_CONFIG.enableDebug) {
        console.log('[RBAC] Role detection:', {
          projectId: currentProjectId,
          customClaims,
          detectedRole,
          trigger,
        });
      }

      setRole(detectedRole, currentProjectId, trigger);
      return true;

    } catch (error) {
      console.error('[RBAC] Error updating role:', error);
      setRole(null, null, trigger);
      return false;
    } finally {
      globalRoleState.value.isLoading = false;
    }
  }

  /**
   * Set role and emit change event
   */
  function setRole(role: UserRole | null, projectId: string | null, trigger: RoleChangeEvent['trigger']): void {
    const oldRole = globalRoleState.value.currentRole;
    const newRole = role;

    globalRoleState.value.currentRole = newRole;
    globalRoleState.value.projectId = projectId;
    globalRoleState.value.lastUpdated = new Date();
    globalRoleState.value.customClaims = userStore.customClaims;

    // Emit role change event
    if (oldRole !== newRole) {
      const event: RoleChangeEvent = {
        oldRole,
        newRole,
        projectId,
        timestamp: new Date(),
        trigger,
      };

      roleChangeCallbacks.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.error('[RBAC] Error in role change callback:', error);
        }
      });

      if (RBAC_CONFIG.enableDebug) {
        console.log('[RBAC] Role changed:', event);
      }
    }
  }

  /**
   * Refresh Firebase ID token and custom claims
   * Includes debouncing to prevent redundant calls
   */
  async function refreshClaims(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshingClaims) {
      console.log('[RBAC] Claims refresh already in progress, skipping');
      return false;
    }

    // Rate limit refresh attempts
    if (lastClaimsRefreshAt && Date.now() - lastClaimsRefreshAt < MIN_CLAIMS_REFRESH_INTERVAL_MS) {
      console.log('[RBAC] Claims refreshed recently, skipping');
      return false;
    }

    // Debounce rapid refresh calls
    if (claimsRefreshTimeout) {
      clearTimeout(claimsRefreshTimeout);
    }

    return new Promise((resolve) => {
      claimsRefreshTimeout = setTimeout(async () => {
        try {
          isRefreshingClaims = true;
          
          const userStore = useUserStore();
          // Use @/firebase barrel which exports the resilient Auth proxy
          const { auth } = await import('@/firebase');
          const currentUser = auth.currentUser;

          if (!currentUser) {
            console.warn('[RBAC] No current user for claims refresh');
            resolve(false);
            return;
          }

          const oldClaims = userStore.customClaims;
          const oldRole = globalRoleState.value.currentRole;

          // Force refresh the ID token
          const idTokenResult = await currentUser.getIdTokenResult(true);

          // Update user store with fresh claims
          userStore.setState({ 
            key: 'customClaims', 
            value: idTokenResult.claims as any 
          });

          if (RBAC_CONFIG.enableDebug) {
            const newClaims = idTokenResult.claims;
            console.log('[RBAC] Claims refreshed successfully');
            console.log('[RBAC] Old claims:', oldClaims);
            console.log('[RBAC] New claims:', newClaims);
            
            // Check if claims actually changed
            const claimsChanged = JSON.stringify(oldClaims) !== JSON.stringify(newClaims);
            if (claimsChanged) {
              console.log('[RBAC] ✅ Claims have changed - updating role...');
            } else {
              console.log('[RBAC] ℹ️  Claims unchanged');
            }
          }

          // Update role with fresh claims
          const success = await updateRole('claims-refresh');
          lastClaimsRefreshAt = Date.now();
          
          if (RBAC_CONFIG.enableDebug) {
            const newRole = globalRoleState.value.currentRole;
            if (oldRole !== newRole) {
              console.log(`[RBAC] ✅ Role updated: ${oldRole} → ${newRole}`);
            } else {
              console.log(`[RBAC] ℹ️  Role unchanged: ${newRole}`);
            }
          }

          resolve(success);

        } catch (error) {
          console.error('[RBAC] Error refreshing claims:', error);
          resolve(false);
        } finally {
          isRefreshingClaims = false;
        }
      }, 100); // 100ms debounce delay
    });
  }

  // ===== DEBUG ROLE OVERRIDE FUNCTIONS =====

  /**
   * Set a debug role override for testing purposes
   */
  function setDebugRoleOverride(role: UserRole): void {
    if (!RBAC_CONFIG.enableDebug) {
      console.warn('[RBAC] Debug role override only available in debug mode');
      return;
    }

    // Store the original role if not already overridden
    if (!debugRoleOverride.value.isActive) {
      debugRoleOverride.value.originalRole = globalRoleState.value.currentRole;
    }

    debugRoleOverride.value.isActive = true;
    debugRoleOverride.value.overrideRole = role;
    debugRoleOverride.value.timestamp = new Date();

    // Emit role change event
    const event: RoleChangeEvent = {
      oldRole: debugRoleOverride.value.originalRole,
      newRole: role,
      projectId: currentProjectId.value,
      timestamp: new Date(),
      trigger: 'manual',
    };

    roleChangeCallbacks.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[RBAC] Error in role change callback:', error);
      }
    });

    if (RBAC_CONFIG.enableDebug) {
      console.log(`[RBAC] 🎭 Debug role override set: ${debugRoleOverride.value.originalRole} → ${role}`);
    }
  }

  /**
   * Clear debug role override and restore original role
   */
  function clearDebugRoleOverride(): void {
    if (!debugRoleOverride.value.isActive) {
      console.log('[RBAC] No debug role override active');
      return;
    }

    const originalRole = debugRoleOverride.value.originalRole;
    const overrideRole = debugRoleOverride.value.overrideRole;

    debugRoleOverride.value.isActive = false;
    debugRoleOverride.value.overrideRole = null;
    debugRoleOverride.value.originalRole = null;
    debugRoleOverride.value.timestamp = null;

    // Emit role change event
    const event: RoleChangeEvent = {
      oldRole: overrideRole,
      newRole: originalRole,
      projectId: currentProjectId.value,
      timestamp: new Date(),
      trigger: 'manual',
    };

    roleChangeCallbacks.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[RBAC] Error in role change callback:', error);
      }
    });

    if (RBAC_CONFIG.enableDebug) {
      console.log(`[RBAC] 🎭 Debug role override cleared: ${overrideRole} → ${originalRole}`);
    }
  }

  /**
   * Get all available roles for debug override
   */
  function getAvailableRoles(): UserRole[] {
    return ['admin', 'owner', 'customer', 'viewer'];
  }

  // ===== PERMISSION CHECKING =====

  /**
   * Check if current user has a specific permission
   */
  function hasPermission(permission: Permission): boolean {
    const role = currentRole.value;
    if (!role) return false;

    return RBAC_HELPERS.hasPermission(role, permission);
  }

  /**
   * Check if current user has any of the specified permissions
   */
  function hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some(permission => hasPermission(permission));
  }

  /**
   * Check if current user has all specified permissions
   */
  function hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every(permission => hasPermission(permission));
  }

  /**
   * Get detailed permission check result
   */
  function checkPermission(permission: Permission): PermissionCheckResult {
    const role = currentRole.value;
    
    if (!role) {
      return {
        hasPermission: false,
        reason: 'No role assigned',
      };
    }

    const hasAccess = RBAC_HELPERS.hasPermission(role, permission);
    
    return {
      hasPermission: hasAccess,
      permissions: hasAccess ? [permission] : [],
      missingPermissions: hasAccess ? [] : [permission],
      reason: hasAccess ? 'Permission granted' : `Role '${role}' does not have permission '${permission}'`,
    };
  }

  // ===== ROLE CHECKING =====

  /**
   * Check if current user has a specific role (or higher)
   */
  function hasRole(requiredRole: UserRole): boolean {
    const role = currentRole.value;
    if (!role) return false;

    return RBAC_HELPERS.hasRole(role, requiredRole);
  }

  /**
   * Check if current user has any of the specified roles (or higher)
   */
  function hasAnyRole(roles: UserRole[]): boolean {
    return roles.some(role => hasRole(role));
  }

  /**
   * Get detailed role check result
   */
  function checkRole(requiredRole: UserRole): RoleCheckResult {
    const role = currentRole.value;
    
    if (!role) {
      return {
        hasAccess: false,
        reason: 'No role assigned',
        fallbackRoute: RBAC_HELPERS.getFallbackRoute(RBAC_CONFIG.defaultRole, currentProjectId.value || undefined),
      };
    }

    const hasAccess = RBAC_HELPERS.hasRole(role, requiredRole);
    
    return {
      hasAccess,
      role,
      reason: hasAccess 
        ? `Role '${role}' meets requirement for '${requiredRole}'`
        : `Role '${role}' does not meet requirement for '${requiredRole}'`,
      fallbackRoute: hasAccess 
        ? undefined 
        : RBAC_HELPERS.getFallbackRoute(role, currentProjectId.value || undefined),
    };
  }

  // ===== CONVENIENCE METHODS =====

  /**
   * Check if current user can access admin areas
   */
  function canAccessAdmin(): boolean {
    const role = currentRole.value;
    if (!role) return false;

    return RBAC_HELPERS.canAccessAdmin(role);
  }

  /**
   * Check if current user is visible in directory
   */
  function isDirectoryVisible(): boolean {
    const role = currentRole.value;
    if (!role) return true; // Default to visible for backward compatibility

    return RBAC_HELPERS.isDirectoryVisible(role);
  }

  /**
   * Check if profile fields are required for current user
   */
  function areProfileFieldsRequired(): boolean {
    const role = currentRole.value;
    if (!role) return true; // Default to required for backward compatibility

    return RBAC_HELPERS.areProfileFieldsRequired(role);
  }

  /**
   * Filter users for directory visibility
   */
  function filterUsersForDirectory(users: any[]): any[] {
    return RBAC_HELPERS.filterUsersForDirectory(users);
  }

  /**
   * Get appropriate fallback route for current user
   */
  function getAuthorizedFallbackRoute(): string {
    const role = currentRole.value || RBAC_CONFIG.defaultRole;
    return RBAC_HELPERS.getFallbackRoute(role, currentProjectId.value || undefined);
  }

  /**
   * Get forbidden route for access denied scenarios
   */
  function getForbiddenRoute(): string {
    return RBAC_HELPERS.getForbiddenRoute(currentProjectId.value || undefined);
  }

  /**
   * Check if current user can access a specific route
   */
  function canAccessRoute(routePath: string): boolean {
    const role = currentRole.value;
    if (!role) return false;

    // Normalize route path (remove project ID prefix if present)
    const normalizedPath = routePath.replace(/^\/[^/]+/, '').replace(/^\//, '') || 'home';

    // Check admin routes
    if (normalizedPath.startsWith('admin')) {
      return canAccessAdmin();
    }

    // Directory route - check visibility
    if (normalizedPath.startsWith('directory')) {
      return isDirectoryVisible();
    }

    // Default routes accessible to all authenticated users
    const publicRoutes = ['home', 'marketplace', 'profile', 'groups'];
    if (publicRoutes.some(route => normalizedPath.startsWith(route))) {
      return true;
    }

    // Default to checking admin access for unknown routes
    return canAccessAdmin();
  }

  // ===== EVENT HANDLING =====

  /**
   * Subscribe to role change events
   */
  function onRoleChange(callback: (event: RoleChangeEvent) => void): () => void {
    roleChangeCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = roleChangeCallbacks.indexOf(callback);
      if (index > -1) {
        roleChangeCallbacks.splice(index, 1);
      }
    };
  }

  // ===== WATCHERS =====

  // Watch for project changes
  watch(
    () => projectStore.projectId,
    (newProjectId, oldProjectId) => {
      if (newProjectId !== oldProjectId) {
        updateRole('project-change');
      }
    },
    { immediate: false }
  );

  // Watch for custom claims changes
  watch(
    () => userStore.customClaims,
    (newClaims, oldClaims) => {
      if (newClaims !== oldClaims) {
        updateRole('claims-refresh');
      }
    },
    { immediate: false }
  );

  // Watch for authentication changes
  watch(
    () => userStore.isAuthenticated,
    (isAuthenticated) => {
      if (isAuthenticated) {
        updateRole('login');
      } else {
        setRole(null, null, 'manual');
      }
    },
    { immediate: false }
  );

  // ===== LIFECYCLE =====

  // Only register lifecycle hooks if we're in a component context
  const currentInstance = getCurrentInstance();
  if (currentInstance) {
    onMounted(() => {
      // Initial role detection
      if (userStore.isAuthenticated && projectStore.projectId) {
        updateRole('login');
      }

      // Auto-refresh claims on mount if configured
      if (RBAC_CONFIG.tokenRefreshOnLoad && userStore.isAuthenticated) {
        refreshClaims();
      }

      // Development mode: More aggressive token refresh
      if (RBAC_CONFIG.enableDebug && userStore.isAuthenticated) {
        // Refresh claims every 30 seconds in development
        const devRefreshInterval = setInterval(async () => {
          if (userStore.isAuthenticated) {
            console.log('[RBAC Dev] Auto-refreshing claims...');
            await refreshClaims();
          } else {
            clearInterval(devRefreshInterval);
          }
        }, 120000); // 2 minutes

        // Clear interval on unmount
        onUnmounted(() => {
          clearInterval(devRefreshInterval);
        });
      }
    });
  }

  // Development convenience: Add global refresh function
  if (RBAC_CONFIG.enableDebug && typeof window !== 'undefined') {
    (window as any).refreshRBACClaims = refreshClaims;
    (window as any).updateRBACRole = (trigger: 'manual' | 'login' | 'project-change' | 'claims-refresh' = 'manual') => updateRole(trigger);
    (window as any).getRBACDebugInfo = getDebugInfo;
    (window as any).setDebugRole = setDebugRoleOverride;
    (window as any).clearDebugRole = clearDebugRoleOverride;
    (window as any).getAvailableRoles = getAvailableRoles;
    console.log('[RBAC Dev] Global functions available:');
    console.log('  • window.refreshRBACClaims() - Force refresh claims');
    console.log('  • window.updateRBACRole() - Force role update');
    console.log('  • window.getRBACDebugInfo() - Get debug info');
    console.log('  • window.setDebugRole(role) - Set debug role override');
    console.log('  • window.clearDebugRole() - Clear debug role override');
    console.log('  • window.getAvailableRoles() - Get available roles');
  }

  // ===== DEBUG HELPERS =====

  /**
   * Get debug information
   */
  function getDebugInfo() {
    return {
      currentRole: currentRole.value,
      projectId: currentProjectId.value,
      isLoading: isLoading.value,
      lastUpdated: lastUpdated.value,
      customClaims: userStore.customClaims,
      isAuthenticated: userStore.isAuthenticated,
      config: RBAC_CONFIG,
      debugOverride: {
        isActive: debugRoleOverride.value.isActive,
        overrideRole: debugRoleOverride.value.overrideRole,
        originalRole: debugRoleOverride.value.originalRole,
        timestamp: debugRoleOverride.value.timestamp,
        actualDetectedRole: globalRoleState.value.currentRole,
      },
    };
  }

  /**
   * Log debug information to console
   */
  function logDebugInfo(): void {
    console.group('=== RBAC Debug Info ===');
    console.log('RBAC Role:', currentRole.value);
    console.log('Project ID:', currentProjectId.value);
    console.log('Is Loading:', isLoading.value);
    console.log('Last Updated:', lastUpdated.value);
    console.log('Is Authenticated:', userStore.isAuthenticated);
    console.log('Custom Claims:', userStore.customClaims);
    console.log('Config:', RBAC_CONFIG);
    console.groupEnd();
  }

  // ===== RETURN COMPOSABLE API =====

  return {
    // State
    currentRole: readonly(currentRole),
    currentProjectId: readonly(currentProjectId),
    isLoading: readonly(isLoading),
    lastUpdated: readonly(lastUpdated),
    isDebugOverrideActive: readonly(isDebugOverrideActive),
    debugOverrideInfo: readonly(debugOverrideInfo),

    // Role detection
    updateRole,
    refreshClaims,
    extractRoleFromClaims,

    // Permission checking
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    checkPermission,

    // Role checking
    hasRole,
    hasAnyRole,
    checkRole,

    // Convenience methods
    canAccessAdmin,
    isDirectoryVisible,
    areProfileFieldsRequired,
    filterUsersForDirectory,
    getAuthorizedFallbackRoute,
    getForbiddenRoute,
    canAccessRoute,

    // Event handling
    onRoleChange,

    // Debug
    getDebugInfo,
    logDebugInfo,
    setDebugRoleOverride,
    clearDebugRoleOverride,
    getAvailableRoles,

    // Helpers (re-export for convenience)
    RBAC_HELPERS,
    ROLE_DEFINITIONS,
    RBAC_CONFIG,
  };
}

/**
 * Get effective role (including debug override)
 * This function ensures that all RBAC utility functions respect debug role overrides.
 * Used by getRBACState() and getRBACUtils() to provide consistent role resolution
 * across navigation, route guards, and UI components.
 * @internal
 */
function getEffectiveRole(): UserRole | null {
  // Return debug override role if active, otherwise return detected role
  if (debugRoleOverride.value.isActive && debugRoleOverride.value.overrideRole) {
    return debugRoleOverride.value.overrideRole;
  }
  return globalRoleState.value.currentRole;
}

/**
 * Lightweight RBAC state access for configuration files
 * Does not set up lifecycle hooks - safe to use in computed functions
 * INCLUDES debug role override support
 */
export function getRBACState() {
  return {
    currentRole: getEffectiveRole(),
    currentProjectId: globalRoleState.value.projectId,
    isLoading: globalRoleState.value.isLoading,
    
    // Simple permission checking without lifecycle hooks
    hasPermission: (permission: Permission): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return RBAC_HELPERS.hasPermission(role, permission);
    },
    
    hasRole: (requiredRole: UserRole): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return RBAC_HELPERS.hasRole(role, requiredRole);
    },
    
    canAccessAdmin: (): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return RBAC_HELPERS.canAccessAdmin(role);
    },
    
    isDirectoryVisible: (): boolean => {
      const role = getEffectiveRole();
      if (!role) return true;
      return RBAC_HELPERS.isDirectoryVisible(role);
    }
  };
}

/**
 * Lightweight RBAC utility for router guards and configuration files
 * Safe to use outside of Vue component setup functions
 * Does not use inject(), onMounted, or other lifecycle hooks
 */
export function getRBACUtils() {
  const userStore = useUserStore();
  const projectStore = useProjectStore();
  
  return {
    // State access
    getCurrentRole: () => getEffectiveRole(),
    getCurrentProjectId: () => globalRoleState.value.projectId,
    getIsLoading: () => globalRoleState.value.isLoading,
    getLastUpdated: () => globalRoleState.value.lastUpdated,
    
    // Authentication state
    isAuthenticated: () => userStore.isAuthenticated,
    getCustomClaims: () => userStore.customClaims,
    
    // Role checking
    hasRole: (requiredRole: UserRole): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return RBAC_HELPERS.hasRole(role, requiredRole);
    },
    
    hasAnyRole: (roles: UserRole[]): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return roles.some(requiredRole => RBAC_HELPERS.hasRole(role, requiredRole));
    },
    
    // Permission checking
    hasPermission: (permission: Permission): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return RBAC_HELPERS.hasPermission(role, permission);
    },
    
    hasAnyPermission: (permissions: Permission[]): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return permissions.some(permission => RBAC_HELPERS.hasPermission(role, permission));
    },
    
    hasAllPermissions: (permissions: Permission[]): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return permissions.every(permission => RBAC_HELPERS.hasPermission(role, permission));
    },
    
    // Convenience methods
    canAccessAdmin: (): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return RBAC_HELPERS.canAccessAdmin(role);
    },
    
    isDirectoryVisible: (): boolean => {
      const role = getEffectiveRole();
      if (!role) return true;
      return RBAC_HELPERS.isDirectoryVisible(role);
    },
    
    areProfileFieldsRequired: (): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      return RBAC_HELPERS.areProfileFieldsRequired(role);
    },
    
    // Route access (legacy - prefer using route meta instead)
    canAccessRoute: (routePath: string): boolean => {
      const role = getEffectiveRole();
      if (!role) return false;
      
      // Normalize route path (remove project ID prefix if present)
      const normalizedPath = routePath.replace(/^\/[^/]+/, '').replace(/^\//, '') || 'home';
      
      // Check if route is reserved
      const segments = normalizedPath.split('/').filter(Boolean);
      if (segments.some(segment => isReservedRoute(segment))) {
        return false;
      }
      
      // Admin routes require admin access
      if (normalizedPath.startsWith('admin')) {
        return RBAC_HELPERS.canAccessAdmin(role);
      }
      
      // Directory route - check visibility
      if (normalizedPath.startsWith('directory')) {
        return RBAC_HELPERS.isDirectoryVisible(role);
      }
      
      // Default routes accessible to all authenticated users
      const publicRoutes = ['home', 'marketplace', 'profile', 'groups'];
      if (publicRoutes.some(route => normalizedPath.startsWith(route))) {
        return true;
      }
      
      // Default to checking admin access for unknown routes
      return RBAC_HELPERS.canAccessAdmin(role);
    },
    
    // Route helpers
    getAuthorizedFallbackRoute: (): string => {
      const role = getEffectiveRole();
      if (!role) return '/login';
      
      if (RBAC_HELPERS.canAccessAdmin(role)) {
        return '/admin';
      }
      
      return '/';
    },
    
    getForbiddenRoute: (): string => {
      return '/forbidden';
    },
    
    // Role extraction (lightweight version) - reuse the cached version
    extractRoleFromClaims: (claims: any, projectId: string): UserRole | null => {
      if (!claims || !projectId) {
        return null;
      }

      // Create cache key (same logic as main function)
      const cacheKey = `${projectId}:${JSON.stringify(claims)}`;
      const now = Date.now();
      
      // Check cache first
      const cached = roleExtractionCache.get(cacheKey);
      if (cached && (now - cached.timestamp) < CACHE_TTL) {
        return cached.role;
      }

      // Demo mode override using centralized detection
      let result: UserRole | null = null;
      try {
        const centralizedDemo = useCentralizedDemo();
        if (centralizedDemo.isDemoMode.value && claims.demo === 'admin') {
          const isShirlyOverrideActive = userStore.isShirlyAdminOverrideActive?.();
          if (!isShirlyOverrideActive) {
            result = 'admin';
          }
        }
      } catch (error) {
        // Fallback to direct claims check if centralized system isn't available
        if (claims.demo === 'admin') {
          const isShirlyOverrideActive = userStore.isShirlyAdminOverrideActive?.();
          if (!isShirlyOverrideActive) {
            result = 'admin';
          }
        }
      }

      // If demo mode didn't return admin, continue with other checks
      if (!result) {
        // Shirly admin override
        if (claims.shirly && userStore.isShirlyAdmin) {
          const isOverrideActive = userStore.isShirlyAdminOverrideActive();
          if (!isOverrideActive) {
            result = 'admin';
          }
        }

        // Format 1: Direct project mapping
        if (!result && claims[projectId] && typeof claims[projectId] === 'string') {
          result = claims[projectId] as UserRole;
        }

        // Format 2: Explicit role mapping
        if (!result) {
          const explicitRoleKey = `${projectId}:role`;
          if (claims[explicitRoleKey]) {
            result = claims[explicitRoleKey] as UserRole;
          }
        }

        // Format 3: Nested project roles
        if (!result && claims.projectRoles && claims.projectRoles[projectId]) {
          result = claims.projectRoles[projectId] as UserRole;
        }

        // Format 4: Nested roles
        if (!result && claims.roles && claims.roles[projectId]) {
          result = claims.roles[projectId] as UserRole;
        }
      }

      // Cache the result
      roleExtractionCache.set(cacheKey, { role: result, timestamp: now });
      
      return result;
    },
    
    // Update role state (for use in guards)
    updateRoleState: (role: UserRole | null, projectId: string | null): void => {
      globalRoleState.value.currentRole = role;
      globalRoleState.value.projectId = projectId;
      globalRoleState.value.lastUpdated = new Date();
      globalRoleState.value.customClaims = userStore.customClaims;
    }
  };
}

// Helper function for route checking
function isReservedRoute(segment: string): boolean {
  const reservedRoutes = [
    'admin',
    'api',
    'auth',
    'login',
    'logout',
    'register',
    'reset-password',
    'verify-email',
    'forbidden',
    'not-found',
    'error'
  ];
  return reservedRoutes.includes(segment.toLowerCase());
} 